/**
 * 모바일 정보 섹션 (일정 인라인 + 작은 썸네일)
 *
 * 모바일에서 사이드바 대신 보여주는 캠페인 일정 정보.
 * 작은 썸네일과 함께 모집 기간, 선정 발표, 미션 일정, 신청자 수를 인라인 표시한다.
 */

import type { PublicCampaignResponse } from '@/lib/seedingPublicApi'
import { formatCompactDate, formatShortDate, getCountdown } from './utils'

interface MobileInfoSectionProps {
  campaign: PublicCampaignResponse
}

export function MobileInfoSection({ campaign }: MobileInfoSectionProps) {
  const countdown = getCountdown(campaign)

  return (
    <div className="flex gap-4">
      {/* 왼쪽: 일정 정보 */}
      <div className="flex-1 space-y-2 text-sm">
        <div>
          <span className="text-xs text-on-surface-variant">모집 기간</span>
          <p className="font-medium">
            {formatCompactDate(campaign.recruit_start_at)} ~{' '}
            {formatCompactDate(campaign.recruit_end_at)}
          </p>
          {countdown && (
            <span className="text-xs text-error font-semibold">
              {countdown}
            </span>
          )}
        </div>

        {campaign.selection_announce_at && (
          <div>
            <span className="text-xs text-on-surface-variant">선정 발표</span>
            <p className="font-medium">
              {formatCompactDate(campaign.selection_announce_at)}
            </p>
          </div>
        )}

        {/* 미션 일정 */}
        {campaign.mission_rounds && campaign.mission_rounds.length > 0 ? (
          <div>
            <span className="text-xs text-on-surface-variant">미션 일정</span>
            <div className="mt-0.5 space-y-0.5">
              {campaign.mission_rounds.map((round) => (
                <p key={round.round} className="font-medium">
                  {round.round}차: ~{formatShortDate(round.deadline_at)}
                  {round.title && (
                    <span className="text-on-surface-variant ml-1">({round.title})</span>
                  )}
                </p>
              ))}
            </div>
          </div>
        ) : campaign.mission_deadline_at ? (
          <div>
            <span className="text-xs text-on-surface-variant">미션 마감</span>
            <p className="font-medium">
              {formatCompactDate(campaign.mission_deadline_at)}
            </p>
          </div>
        ) : null}

        <div>
          <span className="text-xs text-on-surface-variant">신청자</span>
          <p className="font-medium">
            {campaign.application_count} / {campaign.recruit_count}명
          </p>
        </div>
      </div>

      {/* 오른쪽: 작은 썸네일 (thumbnail_url이 있을 때만) */}
      {campaign.thumbnail_url && (
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={campaign.thumbnail_url}
            alt={campaign.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  )
}
